import FormControl from "components/Form/FormControl"
import { AutoCompleteHit, Company } from "models/Company";
import { useFormikContext, getIn } from "formik";
import { useEffect, useRef, useState } from "react";
import { autoCompleteService, locationService } from "services/Services";
import { AutoComplete, AutoCompleteChangeEvent } from "primereact/autocomplete";
import { debounce } from 'lodash';
import { Seo } from "components/Seo";



const Details = () => {

  const { values, errors, setFieldValue, handleChange } = useFormikContext<Company>();

  const [autoCompleteAddress, setAutoCompleteAddress] = useState<AutoCompleteHit[]>([]);

  const fetchAutoCompleteSuggestions = useRef(debounce(async (query: string) => {
    const response = await autoCompleteService.autoCompleteAddress(query);

    if (response !== null && response !== undefined) {
      setAutoCompleteAddress(response.hits);
    }
    else {
      setAutoCompleteAddress([]);
    }
  }, 300));

  const handlePostalCodeChange = async (zip: string, field: string) => {
    if (isValidPostalCode(zip)) {
      locationService.lookupzip(zip).then(
        (data) => {
          setFieldValue(field, data?.postalArea);
        }
      )
    }
    else{
      setFieldValue(field, "");
    }
  }
  
  const handleLocationPostalCodeChange = (zip: string) => handlePostalCodeChange(zip, "location.postalArea");
  const handlePostboxPostalCodeChange = (zip: string) => handlePostalCodeChange(zip, "postBox.postalArea");
  

  const isValidPostalCode = (postalCode: string) => {
    const regex = /^[0-9]{4}$/;
    return regex.test(postalCode);
  }

  const handleOnSelectVisitingAddress = async (hit: AutoCompleteHit) => {

    const response = await locationService.lookupid(hit.id);
    
    if (response !== undefined && response !== null) {
      setFieldValue("location.streetName", response.streetName);
      setFieldValue('location.postalCode', response.postalCode);
      setFieldValue('location.postalArea', response.postalArea);
      
      if (response.streetName.trim() !== hit.street) {
        setFieldValue('location.houseNumber', response.houseNumber);
        setFieldValue('location.houseLetter', response.houseLetter);
      }
      else {
        setFieldValue('location.houseNumber', "");
        setFieldValue('location.houseLetter', "");
      }
    }
    else {
      // fallback in case fetching location fails
      let streetParts = hit.street.split(' ');
      let houseNumberAndHouseLetter = streetParts[streetParts.length - 1];

      streetParts.pop();

      let streetName = streetParts.join(' ');

      let numberMatch = houseNumberAndHouseLetter.match(/\d+/);
      let letterMatch = houseNumberAndHouseLetter.match(/[a-zA-Z]+/);
      let houseNumber = numberMatch ? numberMatch[0] : '';
      let houseLetter = letterMatch ? letterMatch[0] : '';
      
      
      if (houseNumber === ''){
        setFieldValue('location.streetName',hit.street);
      }
      else{
      setFieldValue('location.houseNumber',houseNumber);
      setFieldValue('location.houseLetter',houseLetter);
      setFieldValue('location.streetName', streetName);
      
      }
      setFieldValue('location.postalCode', hit.postalCode);
      setFieldValue('location.postalArea', hit.postalArea);
    }
  }

  const autoCompleteOnChangeHandler = (e:AutoCompleteChangeEvent) => {
      
    fetchAutoCompleteSuggestions.current.cancel();
      if(typeof e.value === "string") {
        setFieldValue('location.streetName', e.value);
      } else {
        setFieldValue('location.streetName', e.value.street);
      }
      
      if (typeof e.value === "string" && e.value.trim().length > 3) {
        fetchAutoCompleteSuggestions.current(e.value)
      }
  }

  const handleEmptyPostboxNumber = () => clearPostboxIfEmpty("number");
  const handleEmptyPostOffice = () => clearPostboxIfEmpty("postOffice");
  const handleEmptyPostalCode = () => clearPostboxIfEmpty("postalCode");

  const clearPostboxIfEmpty = (type : string) => {
    if ((values.postBox?.number === undefined || values.postBox?.number === null || values.postBox?.number.trim() === '' || type === "number")
      && (values.postBox?.postOffice === undefined ||values.postBox?.postOffice === null || values.postBox?.postOffice.trim() === '' || "postOffice")
      && (values.postBox?.postalCode === undefined || values.postBox?.postalCode === null || values.postBox?.postalCode.trim() === '' || "postalCode")
      && values.postBox !== undefined
    ){
    setFieldValue('postBox', null);
    }
  }

  return (
    <><Seo title="Detaljer" />
      <FormControl
        control="input"
        label="Markedsnavn"
        name="name"
        id="Markedsnavn"
        labelclassname="fw-bold"
      />
      <div className="row mb-3">
        <div className="col-md-6">
          <label htmlFor="BesøksadresseGate" className="form-label">Gate</label>
          <div className="mb-3">
            <AutoComplete
              className="d-block"
              inputClassName={`form-control ${Boolean(getIn(errors, "location.streetName")) && "is-invalid"}`}
              id="BesøksadresseGate"
              value={values.location.streetName}
              suggestions={autoCompleteAddress}
              field="name"
              name="location.streetName"
              onChange={(e) => autoCompleteOnChangeHandler(e)}
              onSelect={(e) => { handleOnSelectVisitingAddress(e.value); }}
              completeMethod={(e) => e}
              onKeyPress={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
            />
          </div>
        </div>
        <div className="col-md-3">
          <FormControl
            control="input"
            label="Nummer"
            name="location.houseNumber"
            id="BesøksadresseNummer"
          />
        </div>
        <div className="col-md-3">
          <FormControl
            control="input"
            label="Bokstav"
            name="location.houseLetter"
            id="BesøksadresseBokstav"
          />
        </div>
        <div className="col-md-6">
          <FormControl
            control="input"
            label="Postnummer"
            name="location.postalCode"
            id="BesøksadressePostnummer"
            onChange={(event: { target: { value: string; }; }) => { handleLocationPostalCodeChange(event.target.value); handleChange(event); }}
          />
        </div>
        <div className="col-md-6">
          <FormControl
            control="input"
            label="Poststed"
            name="location.postalArea"
            id="BesøksadressePoststed"
            disabled
          />
        </div>
      </div>
      <p className="fw-bold mt-4">Postadresse</p>
      <div className="row">
        <div className="col-md-6">
          <FormControl
            control="input"
            label="Postboks"
            name="postBox.number"
            id="PostadressePostboks"
            onChange={(event: { target: { value: string; }; }) => { handleChange(event); if (event.target.value === '') handleEmptyPostboxNumber();  }}
          />
        </div>
        <div className="col-md-6">
          <FormControl
            control="input"
            label="Postkontor"
            name="postBox.postOffice"
            id="PostadressePostkontor"
            onChange={(event: { target: { value: string; }; }) => { handleChange(event); if (event.target.value === '') handleEmptyPostOffice(); }}
          />
        </div>
        <div className="col-md-6">
          <FormControl
            control="input"
            label="Postnummer"
            name="postBox.postalCode"
            id="PostadressePostnummer"
            onChange={(event: { target: { value: string; }; }) => { if (event.target.value === ''){ handleEmptyPostalCode(); } else{ handlePostboxPostalCodeChange(event.target.value);} handleChange(event)}}
          />
        </div>
        <div className="col-md-6">
          <FormControl
            control="input"
            label="Poststed"
            name="postBox.postalArea"
            id="PostadressePoststed"
            disabled
          />
        </div>
      </div>
    </>
  )
}

export default Details